import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Footprints, Smile, RefreshCw, Layers, Timer,
  CheckCircle, Target, TreePine, Activity, Quote
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SKILLS = [
  {
    icon: Footprints,
    title: 'The Mindful Walk',
    description: 'It\'s estimated that 95% of golf happens between shots. The walk from one shot to the next is where most mental damage — or most mental gain — occurs. A mindful walk is your opportunity to reset, recover, and prepare for the next moment of execution.',
    onCourse: 'After each shot, take 5 intentional steps. Feel each footfall. Let the previous shot go. Then shift into "gather mode" — observing the next shot\'s conditions with fresh, curious eyes.',
    offCourse: 'Practise walking meditation for 10 minutes. Walk slowly, noticing each phase of each step. When your mind wanders, gently return to the physical sensation of walking. This builds the neural pathways for on-course resets.',
    inApp: 'Track your "In-Between Shots" scores in Performance Scorecards — this directly measures your ability to reset and refocus between shots.',
  },
  {
    icon: Smile,
    title: 'Body Language as a Tool',
    description: 'Your body language between shots isn\'t just a reflection of how you feel — it\'s a tool that shapes how you feel. Research shows that adopting confident body language (head up, shoulders back, purposeful stride) actually reduces cortisol and increases testosterone, even when you don\'t feel confident.',
    onCourse: 'After a bad shot, consciously adjust your posture: head up, shoulders back, walk with purpose. You\'re not pretending — you\'re giving your nervous system the signal that you\'re still competing. Be "the match play guy who\'s hard to crack."',
    offCourse: 'Watch footage of yourself on the course (or imagine it). Notice what your body language says after good vs bad shots. Practise the "confident walk" until it feels natural.',
    inApp: 'Rate "Walking routine & body language" and "Maintaining positive energy" in your In-Between scorecard section. These scores reveal whether body language is a strength or growth area.',
  },
  {
    icon: RefreshCw,
    title: 'The "Next Opportunity" Mindset',
    description: 'Instead of dwelling on the shot you\'ve just hit, immediately shift your thinking to the next opportunity. This isn\'t about suppressing emotion — it\'s about redirecting attention. The next shot is the most important shot of your round, because it\'s the only one you can influence.',
    onCourse: 'After each shot, use a verbal cue: "Next." Or mentally file the shot away: "Done. What\'s next?" Your brain can only focus on one timeline — past or future. Choose the one you can affect.',
    offCourse: 'After each round, review: how many shots did you lose to lingering on previous mistakes? Be honest. Even saving 1-2 shots per round through better recovery adds up over a season.',
    inApp: 'Log "recovery" moments in your Diary — times you successfully moved on from a bad shot. Track whether better In-Between scores correlate with lower actual scores.',
  },
  {
    icon: Layers,
    title: 'Grouping Holes into Threes',
    description: 'One of the most practical mental game tools: instead of thinking about the full 18, group your round into six sets of three holes. This breaks the round into manageable chunks and creates natural mental reset points. You can\'t play 18 holes of intense focus, but you can play 3.',
    onCourse: 'Play holes 1-3 as a "mini round." At the end of the third hole, mentally close that chapter and start fresh. Holes 4-6 are a new game. This prevents snowball effects from bad stretches.',
    offCourse: 'Before your round, divide the course into six groups of three. Set a simple process goal for each group (e.g., "Full routine on every shot" for holes 1-3, "Accept every outcome" for holes 4-6).',
    inApp: 'Use session notes to record how each group of three holes went. Your Reports section will show patterns — do you start strong and fade, or build into the round?',
  },
  {
    icon: Timer,
    title: 'The 10-Second Rule',
    description: 'Give yourself 10 seconds maximum to react to any shot — good or bad. Celebrate, curse, feel the emotion. Then close the door. After 10 seconds, any continued reaction is stealing energy from your next shot.',
    onCourse: 'After a bad shot: feel the frustration for 10 seconds. Let it burn. Then take one deep breath, pick up your tee/divot tool, and walk forward with purpose. The reaction is over. The competition continues.',
    offCourse: 'Practise the 10-second rule in daily life. When something frustrating happens, set a mental timer. Feel it, then move on. Build the habit of controlled emotional duration.',
    inApp: 'Track "Recovery from bad shots" and "Letting go of outcomes" in your Performance Scorecards. These scores directly measure your ability to limit destructive reactions.',
  },
];

const ApproachInBetweenPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="pt-24 pb-20">
        <section className="bg-[#7C3AED] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/the-approach" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to The Approach
            </Link>
            <p className="text-white/50 text-sm uppercase tracking-[0.2em] mb-2">Area Three</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-4">In-Between Shots</h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              Recovery and reset. What happens between shots is where 95% of your round is played — and where the biggest mental gains are waiting.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <section className="py-12 md:py-16" data-testid="what-it-means">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">The 95% You're Ignoring</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-4">
                A round of golf takes roughly 4 hours. The actual time spent executing shots? About <strong>12 minutes</strong>. The remaining 3 hours and 48 minutes — the walking, waiting, watching, thinking — is where your mental game is truly won or lost.
              </p>
              <p className="text-[#57534E] leading-relaxed mb-4">
                This is widely considered the <strong>lowest-hanging fruit</strong> for mental game improvement. Most golfers have never been taught what to do with their minds between shots. They replay mistakes, worry about upcoming holes, calculate scores, and arrive at their next shot mentally exhausted rather than refreshed.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                The best players in the world have learned to use this time intentionally — to recover, reset, and arrive at each shot with a clear, fresh mind. This is a trainable skill.
              </p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="testimonial">
            <div className="bg-[#F2F0E9] border-l-4 border-[#7C3AED] p-6 md:p-8">
              <Quote className="w-8 h-8 text-[#7C3AED]/30 mb-4" />
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "Stop thinking and come to your senses. Between shots, tune into what you can see, hear, and feel around you. This is how you recover."
              </blockquote>
              <p className="text-[#737373] text-sm">— <strong>Karl Morris</strong>, The Mind Factor</p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="the-walk">
            <div className="bg-[#7C3AED] p-6 md:p-10 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-4 text-white">Why Walking Matters</h2>
              <p className="text-white/90 leading-relaxed mb-4">
                Walking the course (rather than riding) isn't just tradition — it's a mental performance tool. The rhythm of walking creates a meditative state that naturally clears mental clutter. It gives your body time to process emotions and your mind time to prepare for the next challenge.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mt-6">
                <div className="bg-white/10 p-4">
                  <p className="text-white font-semibold text-sm mb-1">Physical Reset</p>
                  <p className="text-white/70 text-sm">Walking lowers heart rate and releases tension built up during shot execution.</p>
                </div>
                <div className="bg-white/10 p-4">
                  <p className="text-white font-semibold text-sm mb-1">Mental Clarity</p>
                  <p className="text-white/70 text-sm">The rhythmic movement creates space for your mind to release the previous shot.</p>
                </div>
                <div className="bg-white/10 p-4">
                  <p className="text-white font-semibold text-sm mb-1">Strategic Preparation</p>
                  <p className="text-white/70 text-sm">Walking to your ball gives you time to read conditions and build intention for the next shot.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="skills-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Skills to Train</h2>
            <p className="text-[#57534E] mb-8">Master the space between shots — the hidden key to consistent performance.</p>
            <div className="space-y-8">
              {SKILLS.map((skill, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] overflow-hidden" data-testid={`skill-${i}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#7C3AED] text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <skill.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">{skill.title}</h3>
                        <p className="text-[#57534E] leading-relaxed">{skill.description}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-[#2C5530]/5 p-4">
                        <p className="text-xs text-[#2C5530] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> On the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.onCourse}</p>
                      </div>
                      <div className="bg-[#7C3AED]/5 p-4">
                        <p className="text-xs text-[#7C3AED] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><TreePine className="w-3.5 h-3.5" /> Off the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.offCourse}</p>
                      </div>
                      <div className="bg-[#1A1A1A]/5 p-4">
                        <p className="text-xs text-[#1A1A1A] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Track in QMG</p>
                        <p className="text-sm text-[#57534E]">{skill.inApp}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="pb-8" data-testid="nav-section">
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/the-approach/during" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B]/40 hover:shadow-md transition-all group" data-testid="nav-during">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Previous Area</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#BC562B] transition-colors">During the Round</h3>
                <p className="text-sm text-[#57534E]">Pre-shot routines, focus triggers, and committed swings.</p>
                <ArrowLeft className="w-5 h-5 text-[#BC562B] mt-3" />
              </Link>
              <Link to="/the-approach/after" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#1A1A1A]/30 hover:shadow-md transition-all group" data-testid="nav-after">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Next Area</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#1A1A1A] transition-colors">After the Round</h3>
                <p className="text-sm text-[#57534E]">Reflection, learning, and building momentum for next time.</p>
                <ArrowRight className="w-5 h-5 text-[#1A1A1A] mt-3" />
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApproachInBetweenPage;
